import * as React from 'react';
import { cn } from '../lib/cn';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

interface ToolButton {
  label: string;
  title: string;
  command: string;
  value?: string;
}

const TOOLBAR: ToolButton[] = [
  { label: 'B', title: 'Bold', command: 'bold' },
  { label: 'I', title: 'Italic', command: 'italic' },
  { label: 'U', title: 'Underline', command: 'underline' },
  { label: 'H2', title: 'Heading', command: 'formatBlock', value: 'H2' },
  { label: 'H3', title: 'Subheading', command: 'formatBlock', value: 'H3' },
  { label: '“ ”', title: 'Quote', command: 'formatBlock', value: 'BLOCKQUOTE' },
  { label: '• List', title: 'Bullet list', command: 'insertUnorderedList' },
  { label: '1. List', title: 'Numbered list', command: 'insertOrderedList' },
];

/**
 * Lightweight, dependency-free WYSIWYG editor built on contentEditable +
 * document.execCommand. Emits HTML via onChange. Intended for product
 * descriptions and similar admin/vendor authoring. The HTML is sanitized again
 * server-side before it is stored, so this component is purely for editing UX.
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  className,
  id,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [focused, setFocused] = React.useState(false);

  // Sync external value into the DOM only when it diverges from what the editor
  // already shows — avoids resetting the caret to the start on every keystroke.
  React.useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== value) el.innerHTML = value || '';
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    // Treat visually-empty content as truly empty. Browsers leave placeholder
    // markup like "<br>", "<div><br></div>" or "<p><br></p>" after the user
    // deletes everything; without this, a cleared field persists non-empty
    // whitespace HTML and renders as an empty bordered block downstream.
    const hasText = el.textContent?.trim();
    const hasMedia = el.querySelector('img,hr');
    const html = hasText || hasMedia ? el.innerHTML : '';
    onChange(html);
  };

  const exec = (btn: ToolButton) => (e: React.MouseEvent) => {
    e.preventDefault(); // keep focus in the editable area
    ref.current?.focus();
    document.execCommand(btn.command, false, btn.value);
    emit();
  };

  const addLink = (e: React.MouseEvent) => {
    e.preventDefault();
    ref.current?.focus();
    const url = window.prompt('Link URL (https://…)');
    if (url) document.execCommand('createLink', false, url);
    emit();
  };

  const clearFormat = (e: React.MouseEvent) => {
    e.preventDefault();
    ref.current?.focus();
    document.execCommand('removeFormat');
    document.execCommand('unlink');
    emit();
  };

  // Empty for placeholder purposes if there's no text and no media once tags
  // are stripped — covers "<br>", "<p><br></p>", "<div><br></div>", etc.
  const isEmpty = !/<(img|hr)\b/i.test(value) && value.replace(/<[^>]*>/g, '').trim() === '';

  return (
    <div
      className={cn(
        'rounded-lg border bg-white overflow-hidden',
        focused ? 'border-primary-500 ring-2 ring-primary-500/20' : 'border-slate-200',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        {TOOLBAR.map((btn) => (
          <button
            key={btn.title}
            type="button"
            title={btn.title}
            onMouseDown={exec(btn)}
            className="h-7 min-w-7 px-2 rounded text-xs font-semibold text-slate-600 hover:bg-slate-200 hover:text-slate-900"
          >
            {btn.label}
          </button>
        ))}
        <button
          type="button"
          title="Add link"
          onMouseDown={addLink}
          className="h-7 px-2 rounded text-xs font-semibold text-slate-600 hover:bg-slate-200 hover:text-slate-900"
        >
          Link
        </button>
        <button
          type="button"
          title="Clear formatting"
          onMouseDown={clearFormat}
          className="h-7 px-2 rounded text-xs font-semibold text-slate-600 hover:bg-slate-200 hover:text-slate-900"
        >
          Clear
        </button>
      </div>

      <div className="relative">
        {isEmpty && placeholder && (
          <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-slate-400">
            {placeholder}
          </span>
        )}
        <div
          id={id}
          ref={ref}
          contentEditable
          role="textbox"
          aria-multiline="true"
          onInput={emit}
          onBlur={() => { setFocused(false); emit(); }}
          onFocus={() => setFocused(true)}
          className="prose prose-sm max-w-none min-h-[140px] px-3 py-2.5 text-sm text-slate-800 focus:outline-none"
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
};
